import React, { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import Button from '@/components/ui/Button'
import { designTokens } from '@/utils/design-tokens'

interface DeleteConfirmModalProps {
  isOpen: boolean
  onClose: () => void
  /** 확인 시 호출. showMemo가 true면 입력된 메모(선택)를 인자로 전달 */
  onConfirm: (memo?: string) => void
  title?: string
  message?: string
  confirmText?: string
  cancelText?: string
  isDeleting?: boolean
  error?: string | null
  /** 삭제 사유 메모 입력란 표시 여부 */
  showMemo?: boolean
  memoLabel?: string
  memoPlaceholder?: string
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = '전략과제 삭제',
  message = '전략과제를 삭제하시겠습니까?\n하위 항목까지 모두 삭제됩니다.',
  confirmText = '삭제',
  cancelText = '취소',
  isDeleting = false,
  error = null,
  showMemo = false,
  memoLabel = '메모 (선택)',
  memoPlaceholder = '삭제 사유 등 메모를 남길 수 있습니다. (선택)'
}) => {
  const [memo, setMemo] = useState('')

  // 모달이 닫히면 메모 초기화 (다음에 다시 열 때 깨끗한 상태)
  useEffect(() => {
    if (!isOpen) setMemo('')
  }, [isOpen])

  if (!isOpen) return null

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  const handleConfirm = () => {
    onConfirm(showMemo ? (memo.trim() || undefined) : undefined)
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      data-modal-overlay
      onMouseDown={(e) => e.stopPropagation()}
      onClick={handleBackdropClick}
    >
      <div
        className="bg-white rounded"
        style={{
          width: '400px',
          padding: '20px',
          border: `1px solid ${designTokens.colors.gray[1]}`,
          borderRadius: '4px'
        }}
      >
        {/* Header */}
        <div
          className="flex justify-between items-center"
          style={{
            padding: '7px 0',
            borderBottom: `1px solid ${designTokens.colors.gray[1]}`,
            marginBottom: '16px'
          }}
        >
          <h2
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '24px',
              fontWeight: 700,
              lineHeight: '1.193359375em',
              color: designTokens.colors.gray[6]
            }}
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            disabled={isDeleting}
            className="p-0 border-0 bg-transparent cursor-pointer"
            style={{
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X
              size={12}
              style={{
                color: designTokens.colors.gray[5],
                strokeWidth: 2
              }}
            />
          </button>
        </div>

        {/* Content */}
        <div
          className="flex flex-col items-center justify-center"
          style={{
            padding: showMemo ? '12px 0' : '20px 0',
            marginBottom: '16px'
          }}
        >
          <p
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '18px',
              fontWeight: 600,
              lineHeight: '1.193359375em',
              color: designTokens.colors.black,
              textAlign: 'center',
              whiteSpace: 'pre-line'
            }}
          >
            {message}
          </p>
        </div>

        {/* Memo (선택) */}
        {showMemo && (
          <div className="flex flex-col gap-2" style={{ marginBottom: '16px' }}>
            <label
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '14px',
                fontWeight: 600,
                color: designTokens.colors.gray[6]
              }}
            >
              {memoLabel}
            </label>
            <textarea
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              disabled={isDeleting}
              placeholder={memoPlaceholder}
              rows={3}
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: '14px',
                color: designTokens.colors.gray[6],
                border: `1px solid ${designTokens.colors.gray[1]}`,
                borderRadius: '4px',
                padding: '8px 10px',
                resize: 'vertical',
                outline: 'none'
              }}
            />
          </div>
        )}

        {/* Error */}
        {error && (
          <p
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: '13px',
              color: designTokens.colors.error.DEFAULT,
              marginBottom: '12px',
              whiteSpace: 'pre-line'
            }}
          >
            {error}
          </p>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2.5">
          <Button
            size="medium"
            color="light"
            onClick={onClose}
            disabled={isDeleting}
            style={{
              padding: '0 10px',
              height: '40px',
              backgroundColor: designTokens.colors.gray[1],
              borderColor: designTokens.colors.gray[1],
              color: designTokens.colors.gray[6]
            }}
          >
            {cancelText}
          </Button>
          <Button
            size="medium"
            color="danger"
            onClick={handleConfirm}
            disabled={isDeleting}
            style={{
              padding: '0 10px',
              height: '40px',
              backgroundColor: designTokens.colors.error.DEFAULT,
              borderColor: designTokens.colors.error.DEFAULT,
              color: designTokens.colors.white
            }}
          >
            {isDeleting ? '삭제 중...' : confirmText}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default DeleteConfirmModal
